/**
 * Browser-side detection of the user's timezone, locale, and currency.
 *
 * These wrap browser-only globals (`Intl`, `navigator`), so they live in the
 * client. The fallback values and the pure locale→currency mapping come from
 * the shared `@elara/domain/locale` module, so the client and the server (which
 * feeds the AI concierge prompt) agree on the canonical defaults.
 */

import { currencyForLocale, DEFAULT_LOCALE, DEFAULT_TIMEZONE } from '@elara/domain/locale';

/**
 * Detects the user's timezone using the browser's Intl API.
 * Falls back to the canonical {@link DEFAULT_TIMEZONE} if detection fails.
 */
export function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;
  } catch {
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Detects the user's locale using the browser's navigator API.
 * Falls back to the canonical {@link DEFAULT_LOCALE} if detection fails.
 */
export function detectLocale(): string {
  try {
    return navigator.language || navigator.languages?.[0] || DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

/**
 * Detects timezone, currency, and locale from the user's browser settings.
 * Currency is derived from the detected locale via the shared mapping.
 */
export function detectLocalePreferences(): {
  timezone: string;
  currency: string;
  locale: string;
} {
  const locale = detectLocale();
  return {
    timezone: detectTimezone(),
    currency: currencyForLocale(locale),
    locale,
  };
}
